import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';

const getErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const msg = (d.msg || d.message || '').toString();
        // Friendly rewrite for Pydantic EmailStr validation errors
        if (/email/i.test(msg) || /@-sign/i.test(msg)) {
          return 'Please enter a valid email address.';
        }
        return msg || JSON.stringify(d);
      })
      .join(', ');
  }
  return fallback;
};

const cleanEmail = (e) => (e || '').trim().toLowerCase();

function useLoginForm(loginUser, email, password, setVerifyEmail, setView, triggerResend) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleaned = cleanEmail(email);
      const response = await api.post('/auth/login', { email: cleaned, password });
      toast.success('Login successful!');
      loginUser(response.data.token, response.data.user);
      navigate('/');
    } catch (error) {
      const msg = getErrorMessage(error, 'Login failed');
      // Auto-redirect to verify view if the user just needs to verify their email
      if (/not verified|email\s*not\s*verified/i.test(msg)) {
        const cleaned = cleanEmail(email);
        setVerifyEmail(cleaned);
        setView('verify');
        toast.warning("Almost there — verify your email first. Check your inbox (and spam folder) for a code.", { duration: 8000 });
        try {
          await triggerResend(cleaned);
        } catch {
          // ignore; user can hit Resend manually
        }
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, handleLogin };
}

function useRegisterForm(loginUser, setView, setVerifyEmail, setVerifyCode, email, password) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!ageConfirmed) { toast.error('Please confirm you are 13 years or older'); return; }
    setLoading(true);
    try {
      const cleaned = cleanEmail(email);
      const response = await api.post('/auth/register', { email: cleaned, password, name: (name || '').trim(), nickname: nickname || 'friend' });
      setVerifyEmail(cleaned);
      if (response.data.verification_code) {
        // Email delivery failed — surface the code prominently so the user is unblocked
        setVerifyCode(response.data.verification_code);
        toast.warning(`Email delivery hiccupped. Your code: ${response.data.verification_code}`, { duration: 15000 });
      } else {
        toast.success("Almost there — check your inbox for a verification code (look in spam too).");
      }
      setView('verify');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return { name, setName, nickname, setNickname, ageConfirmed, setAgeConfirmed, loading, handleRegister };
}

function useVerifyForm(loginUser, verifyEmail, verifyCode, setVerifyCode) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-email', { email: cleanEmail(verifyEmail), code: (verifyCode || '').trim() });
      toast.success('Email verified!');
      loginUser(response.data.token, response.data.user);
      navigate('/');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (overrideEmail) => {
    try {
      const target = cleanEmail(overrideEmail || verifyEmail);
      const response = await api.post('/auth/resend-verification', { email: target });
      if (response.data.verification_code) {
        setVerifyCode(response.data.verification_code);
        toast.warning(`Email delivery hiccupped. Your code: ${response.data.verification_code}`, { duration: 15000 });
      } else {
        toast.success("Fresh code sent. Check your inbox (and spam folder).");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to resend code'));
    }
  };

  return { loading, handleVerify, handleResend };
}

function usePasswordResetForm(setView) {
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');

  const handleForgot = async (e) => {
    e.preventDefault();
    try {
      const cleaned = cleanEmail(email);
      await api.post('/auth/forgot-password', { email: cleaned });
      toast.success('If that email is registered, a reset link was sent. Check your inbox.');
      setVerifyEmail(cleaned);
      setView('reset');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reset token'));
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/reset-password', { email: cleanEmail(verifyEmail), token: (resetToken || '').trim(), new_password: newPassword });
      toast.success('Password reset! Please login.');
      setView('login');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Reset failed'));
    }
  };

  return { email, setEmail, verifyEmail, resetToken, setResetToken, newPassword, setNewPassword, handleForgot, handleReset };
}

export function useAuthForms(loginUser) {
  const [view, setView] = useState('login');
  const [sharedVerifyEmail, setSharedVerifyEmail] = useState('');
  // Shared between login and register so switching tabs keeps values
  // AND so the React state binding actually matches the form being shown.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');

  const verify = useVerifyForm(loginUser, sharedVerifyEmail || email, verifyCode, setVerifyCode);
  // Login auto-redirects to verify view when account exists but isn't verified,
  // and triggers a fresh code resend so users always have a path forward.
  const login = useLoginForm(loginUser, email, password, setSharedVerifyEmail, setView, verify.handleResend);
  const register = useRegisterForm(loginUser, setView, setSharedVerifyEmail, setVerifyCode, email, password);
  const reset = usePasswordResetForm(setView);

  return {
    view, setView,
    // Shared email/password
    email, setEmail,
    password, setPassword,
    showPassword, setShowPassword,
    loading: login.loading || register.loading || verify.loading,
    handleLogin: login.handleLogin,
    // Register-only
    name: register.name, setName: register.setName,
    nickname: register.nickname, setNickname: register.setNickname,
    ageConfirmed: register.ageConfirmed, setAgeConfirmed: register.setAgeConfirmed,
    handleRegister: register.handleRegister,
    // Verify
    verifyEmail: sharedVerifyEmail || email,
    verifyCode, setVerifyCode,
    handleVerify: verify.handleVerify,
    handleResend: verify.handleResend,
    // Reset
    resetToken: reset.resetToken, setResetToken: reset.setResetToken,
    newPassword: reset.newPassword, setNewPassword: reset.setNewPassword,
    handleForgot: reset.handleForgot,
    handleReset: reset.handleReset,
  };
}
